import React, { useMemo, useState, useEffect } from "react";
import { DayPost } from "../../types/post";
import { generateDateID, getDayPost } from "../../services/dayPostService";
import { useAuth } from "../../contexts/AuthContext";

interface WellnessDaysProps {
  dayPosts: DayPost[];
  className?: string;
  acuteWellness?: number; // wellnessA from peakScoreLog (acute wellness score)
}

const WellnessDays: React.FC<WellnessDaysProps> = ({
  dayPosts,
  className = "",
  acuteWellness,
}) => {
  const { currentUser } = useAuth();

  // State for 28-day data fetching
  const [extended28DayData, setExtended28DayData] = useState<DayPost[]>([]);
  const [loading28DayData, setLoading28DayData] = useState(false);
  const [has28DayDataLoaded, setHas28DayDataLoaded] = useState(false);

  // Effect to fetch 28-day data when component mounts
  useEffect(() => {
    const fetch28DayData = async () => {
      if (!currentUser?.uid || loading28DayData || has28DayDataLoaded) return;

      setLoading28DayData(true);

      try {
        const today = new Date();
        const dateIds: string[] = [];

        // Generate last 28 days worth of dateIDs
        for (let i = 27; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(today.getDate() - i);
          dateIds.push(generateDateID(date));
        }

        // Fetch data for dates that aren't already in dayPosts
        const existingDateIds = new Set(dayPosts.map((post) => post.dateID));
        const missingDateIds = dateIds.filter(
          (dateId) => !existingDateIds.has(dateId),
        );

        // console.log(
        //   `[WellnessDays-28Day] Fetching ${missingDateIds.length} missing days for 28-day comparison`,
        // );

        // Fetch missing days
        const missingDayPosts = await Promise.all(
          missingDateIds.map(async (dateId) => {
            try {
              const dayPost = await getDayPost(currentUser.uid, dateId);
              return dayPost;
            } catch (error) {
              // console.warn(
              //   `[WellnessDays-28Day] Failed to fetch ${dateId}:`,
              //   error,
              // );
              return null;
            }
          }),
        );

        // Filter out nulls and combine with existing data
        const validMissingPosts = missingDayPosts.filter(
          (post): post is DayPost => post !== null,
        );
        const combinedData = [...dayPosts, ...validMissingPosts];

        setExtended28DayData(combinedData);
        setHas28DayDataLoaded(true);

        // console.log(
        //   `[WellnessDays-28Day] Successfully loaded ${validMissingPosts.length} additional days`,
        // );
      } catch (error) {
        // console.error(
        //   "[WellnessDays-28Day] Error fetching 28-day data:",
        //   error,
        // );
        // Fallback to existing data
        setExtended28DayData(dayPosts);
        setHas28DayDataLoaded(true);
      } finally {
        setLoading28DayData(false);
      }
    };

    fetch28DayData();
  }, [currentUser?.uid, dayPosts, loading28DayData, has28DayDataLoaded]);

  // Helper function to get wellness color based on score (10-80 range)
  const getWellnessColor = (score: number): string => {
    const greenShades = [
      "#dcfce7", // lightest - low scores
      "#bbf7d0",
      "#86efac",
      "#4ade80",
      "#22c55e",
      "#16a34a",
      "#15803d", // darkest - high scores
    ];

    // Map score from 10-80 range to 0-6 index
    if (score < 10) return greenShades[0];
    if (score >= 80) return greenShades[6];

    // Linear interpolation between 10 and 80
    const normalizedScore = (score - 10) / (80 - 10); // 0 to 1
    const index = Math.floor(normalizedScore * 6);
    return greenShades[Math.min(index, 6)];
  };

  // Calculate wellness days for the past week with 28-day comparison
  const {
    wellnessWeekData,
    wellnessDaysCount,
    percentageDifference,
    chronicAverage,
  } = useMemo(() => {
    const today = new Date();
    const weekDays = [];

    // Generate past 7 days (including today)
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);

      const dateID = generateDateID(date);
      const dayName = date
        .toLocaleDateString("en-US", { weekday: "narrow" })
        .toUpperCase();

      // Find if this day has wellness logs and get the score
      const dayPost = dayPosts.find((post) => post.dateID === dateID);
      const hasWellnessLog =
        dayPost && dayPost.wellnessLogs && dayPost.wellnessLogs.length > 0;

      // Get the wellness score from the latest log
      const wellnessScore = hasWellnessLog
        ? dayPost.wellnessLogs[dayPost.wellnessLogs.length - 1].wellnessScore ||
          0
        : 0;

      weekDays.push({
        dateID,
        dayName,
        hasWellnessLog: !!hasWellnessLog,
        wellnessScore,
        isToday: dateID === generateDateID(today),
      });
    }

    // Calculate 28-day comparison using extended data when available
    const dataSourceForComparison = has28DayDataLoaded
      ? extended28DayData
      : dayPosts;
    let comparisonPeriodTotal = 0;
    let actualDaysFound = 0;

    // Always check 28 days when we have extended data
    const daysToCheck = has28DayDataLoaded
      ? 28
      : Math.min(28, dayPosts.length * 4);

    for (let i = daysToCheck - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateID = generateDateID(date);

      const dayPost = dataSourceForComparison.find(
        (post) => post.dateID === dateID,
      );
      if (dayPost && dayPost.wellnessLogs && dayPost.wellnessLogs.length > 0) {
        actualDaysFound++;
        const wellnessScore =
          dayPost.wellnessLogs[dayPost.wellnessLogs.length - 1].wellnessScore ||
          0;
        comparisonPeriodTotal += wellnessScore;
      }
    }

    // Calculate averages
    const last7DaysTotal = weekDays.reduce(
      (sum, day) => sum + (day.hasWellnessLog ? day.wellnessScore : 0),
      0,
    );
    const last7DaysCount = weekDays.filter((day) => day.hasWellnessLog).length;

    const comparisonPeriodAverage =
      actualDaysFound > 0 ? comparisonPeriodTotal / actualDaysFound : 0;
    const last7DaysAverage =
      last7DaysCount > 0 ? last7DaysTotal / last7DaysCount : 0;

    // Calculate percentage difference
    const percentDiff = (() => {
      // If no wellness logs in either period, show 0%
      if (comparisonPeriodAverage === 0 && last7DaysAverage === 0) return 0;

      // No prior days to compare against at all - there is no baseline yet,
      // so don't claim a ±100% swing.
      if (actualDaysFound === 0) return 0;

      // If we don't have enough comparison data, show a simple indicator
      if (actualDaysFound < 7) {
        if (last7DaysAverage > comparisonPeriodAverage) return 100;
        if (last7DaysAverage < comparisonPeriodAverage) return -100;
        return 0;
      }

      // If baseline is too small, don't show percentage
      const minThreshold = 10;
      if (comparisonPeriodAverage < minThreshold) {
        if (last7DaysAverage > comparisonPeriodAverage) return 100;
        if (last7DaysAverage < comparisonPeriodAverage) return -100;
        return 0;
      }

      // If no recent wellness but baseline exists, show as negative change
      if (last7DaysAverage === 0) return -100;

      // Normal calculation with cap at ±200%
      const rawPercent =
        ((last7DaysAverage - comparisonPeriodAverage) /
          comparisonPeriodAverage) *
        100;
      return Math.max(-200, Math.min(200, rawPercent));
    })();

    // Debug logging
    // console.log(`[WellnessDays] Calculation Summary:`);
    // console.log(`  Available dayPosts: ${dayPosts.length}`);
    // console.log(`  Extended 28-day data loaded: ${has28DayDataLoaded}`);
    // console.log(`  Days found in comparison period: ${actualDaysFound}`);
    // console.log(`  7-day count: ${last7DaysCount}`);
    // console.log(`  7-day average: ${last7DaysAverage.toFixed(1)}`);
    // console.log(
    //   `  Comparison period average: ${comparisonPeriodAverage.toFixed(1)}`,
    // );
    // console.log(`  Percentage difference: ${percentDiff.toFixed(1)}%`);

    return {
      wellnessWeekData: weekDays,
      wellnessDaysCount: last7DaysCount,
      percentageDifference: percentDiff,
      chronicAverage: comparisonPeriodAverage,
    };
  }, [dayPosts, has28DayDataLoaded, extended28DayData]);

  // Calculate average wellness score for the week
  const averageWellnessScore = useMemo(() => {
    const totalScore = wellnessWeekData.reduce(
      (sum, day) => sum + (day.hasWellnessLog ? day.wellnessScore : 0),
      0,
    );
    return wellnessDaysCount > 0 ? totalScore / wellnessDaysCount : 0;
  }, [wellnessWeekData, wellnessDaysCount]);

  return (
    <div className={`rounded-lg ${className}`}>
      <div className="flex items-center justify-between">
        {/* Left side - Average score and comparison */}
        <div className="flex-1">
          <h3 className="text-sm font-grotesk mb-2">
            Wellness{" "}
            <span className="font-grotesk text-base-content/60 font-light">
              Monitoring
            </span>
          </h3>
          {(() => {
            // A Peak Score of 0 means no prior-day data to average, not an
            // actual acute wellness reading - fall back to the logged
            // average instead of displaying a false "0".
            const hasAcuteBaseline =
              acuteWellness !== undefined && acuteWellness > 0;
            const diff =
              hasAcuteBaseline && chronicAverage > 0
                ? ((acuteWellness - chronicAverage) / chronicAverage) * 100
                : percentageDifference;
            return (
              <>
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-5xl font-bold text-base-content font-heading">
                    {Math.round(
                      hasAcuteBaseline ? acuteWellness : averageWellnessScore,
                    )}
                  </span>

                  <span
                    className={`text-sm font-medium px-2 py-1 rounded-md ${
                      diff > 0
                        ? "bg-green-500/15 text-green-700"
                        : diff < 0
                          ? "bg-red-500/15 text-red-700"
                          : "bg-base-200 text-base-content/80"
                    }`}
                  >
                    {diff > 0 ? "+" : ""}
                    {Math.round(diff)}%
                  </span>
                </div>
                <p className="text-xs text-base-content/60">
                  {hasAcuteBaseline
                    ? "Acute Wellness Score"
                    : "Last 7 Days average"}
                </p>
              </>
            );
          })()}
        </div>

        {/* Right side - Week visualization */}
        <div className="flex flex-col items-center mt-2">
          <div className="flex space-x-1">
            {wellnessWeekData.map((day) => (
              <div
                key={day.dateID}
                className={`w-4 h-12 rounded-full flex items-center justify-center relative ${
                  day.hasWellnessLog
                    ? "text-white"
                    : "bg-base-300 text-base-content/50"
                }`}
                style={
                  day.hasWellnessLog
                    ? { backgroundColor: getWellnessColor(day.wellnessScore) }
                    : undefined
                }
              >
                {/* Checkmark for wellness logged days */}
                {day.hasWellnessLog && (
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
            ))}
          </div>

          {/* Day labels */}
          <div className="flex space-x-1">
            {wellnessWeekData.map((day) => (
              <div key={`${day.dateID}-label`} className="w-4 text-center">
                <span
                  className={`text-xs font-medium ${
                    day.isToday
                      ? "text-base-content font-semibold"
                      : "text-base-content/60"
                  }`}
                >
                  {day.dayName}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessDays;
